import type { BrandKey } from '../config/brands';
import type { ProgramLocation } from './location';
import { isPaymentInFlight } from '../utils/cart';

/**
 * Everything that varies per program type lives in ONE table: display name, the
 * decorative CJK glyph, the emoji icon, the brand accent color, and the Tailwind
 * badge classes. Adding a program type means adding one row here — the union type,
 * the canonical sort order, the admin dropdown, and every display helper below are
 * all derived from this array, so they can't drift apart.
 *
 * Array order is the canonical order: it drives section grouping/sorting
 * (`compareProgramTypes`) and the admin type dropdown (`PROGRAM_TYPE_OPTIONS`).
 *
 * `color` is the marketing-palette accent (see bhcskids.com/school-year). It is
 * `null` for types with no brand color of their own; each surface then supplies its
 * own neutral — see `getProgramTypeColor` vs `getProgramTypeSectionColor`.
 */
interface ProgramTypeMetaShape {
    readonly value: string;
    readonly label: string;
    readonly glyph: string;
    readonly icon: string;
    readonly color: string | null;
    readonly badge: string;
}

const BADGE_NEUTRAL = 'border border-gray-300 text-gray-600 bg-gray-50';

export const PROGRAM_TYPES = [
    { value: 'academic', label: 'Academic', glyph: '学', icon: '📚', color: '#a42423', badge: 'border border-indigo-400 text-indigo-700 bg-indigo-50' },
    { value: 'extracurricular', label: 'Extracurricular', glyph: '趣', icon: '🎨', color: '#42563b', badge: 'border border-purple-400 text-purple-700 bg-purple-50' },
    { value: 'language', label: 'Language', glyph: '文', icon: '🌍', color: '#42563b', badge: 'border border-green-400 text-green-700 bg-green-50' },
    { value: 'artistic', label: 'Artistic', glyph: '艺', icon: '🎭', color: '#ae4516', badge: 'border border-pink-400 text-pink-700 bg-pink-50' },
    { value: 'after-school', label: 'After School', glyph: '课', icon: '🧸', color: '#8aaeb2', badge: 'border border-blue-400 text-blue-700 bg-blue-50' },
    { value: 'logic-strategy', label: 'Logic & Strategy', glyph: '弈', icon: '🧩', color: '#244868', badge: 'border border-cyan-400 text-cyan-700 bg-cyan-50' },
    { value: 'camp', label: 'Camp', glyph: '营', icon: '🏕️', color: '#f2a13e', badge: 'border border-orange-400 text-orange-700 bg-orange-50' },
    { value: 'sports', label: 'Sports', glyph: '竞', icon: '🏅', color: '#ae4516', badge: 'border border-red-400 text-red-700 bg-red-50' },
    { value: 'registration', label: 'Registration', glyph: '录', icon: '✍️', color: '#7c6a46', badge: 'border border-amber-400 text-amber-700 bg-amber-50' },
    { value: 'other', label: 'Other', glyph: '余', icon: '📝', color: null, badge: BADGE_NEUTRAL },
] as const satisfies readonly ProgramTypeMetaShape[];

export type ProgramType = typeof PROGRAM_TYPES[number]['value'];
export type ProgramTypeMeta = ProgramTypeMetaShape;

/**
 * Legacy `programs.type` values that still exist in the database but are not offered
 * in the UI. Kept out of PROGRAM_TYPES so they never appear in the dropdown or as a
 * section header, while existing rows still render with the right icon/glyph/color.
 */
const LEGACY_PROGRAM_TYPES: readonly ProgramTypeMetaShape[] = [
    // Predecessor of 'artistic'. Keeps its own 🎨 icon (what it has always rendered).
    { value: 'arts', label: 'Arts', glyph: '艺', icon: '🎨', color: '#ae4516', badge: BADGE_NEUTRAL },
];

/**
 * `programs.type` values that belong to a brand other than BHCS.
 *
 * Deliberately NOT in `PROGRAM_TYPES`: that array is the canonical BHCS vocabulary and drives
 * `PROGRAM_TYPE_ORDER` (BHCS section order) and `PROGRAM_TYPE_OPTIONS` (the admin dropdown).
 * Folding Kaleido's four categories into it would put "Specialty Workshop" in the BHCS admin
 * type picker and give it a section header on the BHCS catalog — neither of which should
 * happen. They are in the lookup map below so that every display helper (label, glyph, icon,
 * accent, badge) resolves them wherever a row is rendered, including admin lists.
 *
 * `programs.type` has no CHECK constraint, so these values need no migration; the Kaleido
 * catalog's own section order comes from `BRAND_PROFILES.kaleido.programTypeOrder`.
 */
export const KALEIDO_PROGRAM_TYPES = [
    { value: 'kaleido-enrichment', label: 'Enrichment Series', glyph: '趣', icon: '🎨', color: '#7c3aed', badge: 'border border-violet-400 text-violet-700 bg-violet-50' },
    { value: 'kaleido-workshop', label: 'Specialty Workshop', glyph: '坊', icon: '🧩', color: '#0891b2', badge: 'border border-cyan-400 text-cyan-700 bg-cyan-50' },
    { value: 'kaleido-readiness', label: 'School Readiness', glyph: '启', icon: '🔤', color: '#ea580c', badge: 'border border-orange-400 text-orange-700 bg-orange-50' },
    { value: 'kaleido-community', label: 'Community Event', glyph: '聚', icon: '🎈', color: '#16a34a', badge: 'border border-green-400 text-green-700 bg-green-50' },
] as const satisfies readonly ProgramTypeMetaShape[];

const PROGRAM_TYPE_BY_VALUE: ReadonlyMap<string, ProgramTypeMetaShape> = new Map(
    [...PROGRAM_TYPES, ...LEGACY_PROGRAM_TYPES, ...KALEIDO_PROGRAM_TYPES].map(meta => [meta.value, meta])
);

/** Metadata for a raw `programs.type` value, or undefined when unrecognized. */
export function getProgramTypeMeta(type: string): ProgramTypeMeta | undefined {
    return PROGRAM_TYPE_BY_VALUE.get((type || '').toLowerCase());
}

/** Canonical program types in display order. */
export const PROGRAM_TYPE_ORDER: readonly ProgramType[] = PROGRAM_TYPES.map(m => m.value);

/** Options for the admin program-type `<select>`, in canonical order. */
export const PROGRAM_TYPE_OPTIONS: readonly { value: ProgramType; label: string }[] =
    PROGRAM_TYPES.map(({ value, label }) => ({ value, label }));

/** Options for a Kaleido program-type `<select>`, in catalog order. */
export const KALEIDO_PROGRAM_TYPE_OPTIONS: readonly { value: string; label: string }[] =
    KALEIDO_PROGRAM_TYPES.map(({ value, label }) => ({ value, label }));

/**
 * The program types a brand actually sells, for an admin type picker.
 *
 * Each brand has its own vocabulary and they do not overlap: "After School" is meaningless
 * for Kaleido, "Specialty Workshop" for BHCS. Offering the union would let an admin file a
 * Kaleido workshop under a BHCS section header, and offering only `PROGRAM_TYPE_OPTIONS` is
 * what made the dropdown render blank on a Kaleido program — its current value was not in
 * the list, so touching the control silently rewrote the type.
 */
export function programTypeOptionsFor(brand: BrandKey): readonly { value: string; label: string }[] {
    return brand === 'kaleido' ? KALEIDO_PROGRAM_TYPE_OPTIONS : PROGRAM_TYPE_OPTIONS;
}

/** The type a new program of this brand starts on — the first its brand offers. */
export function defaultProgramTypeFor(brand: BrandKey): string {
    return programTypeOptionsFor(brand)[0].value;
}

/** Whether `type` is one this brand sells. False means the picker would render blank. */
export function isProgramTypeOfBrand(type: string, brand: BrandKey): boolean {
    return programTypeOptionsFor(brand).some(o => o.value === type);
}

/**
 * Sort comparator for grouping programs into type sections. Canonical types come
 * first in PROGRAM_TYPES order; anything unrecognized sorts last, alphabetically.
 */
export function compareProgramTypes(a: string, b: string): number {
    const rank = (t: string): number => {
        const i = PROGRAM_TYPE_ORDER.indexOf(t as ProgramType);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return rank(a) - rank(b) || a.localeCompare(b);
}

/**
 * `programs.enrollment_status` — whether a program accepts NEW enrollment.
 *
 * Deliberately separate from `programs.active`, which answers a different question: whether the
 * program is operational at all. `active` drives monthly invoice generation, staff attendance and
 * schedule screens, admin rosters and analytics; closing signups must not touch any of that. The
 * two combine as:
 *
 *   active: true,  enrollment_status: 'open'    → running, accepting new students
 *   active: true,  enrollment_status: 'closed'  → running and billing, closed to new signups
 *   active: false                               → not operational; enrollment_status irrelevant
 */
export type ProgramEnrollmentStatus = 'open' | 'closed';

/**
 * Whether a program has been locked to new signups. Reads the enrollment dimension ONLY — it says
 * nothing about `active`, which is a separate question. Use this on surfaces that already show
 * only active programs (the parent catalog, a program's own detail page) to decide whether to
 * badge and disable the enroll affordance.
 *
 * A missing/null `enrollment_status` reads as unlocked, so rows selected without the column, and
 * fixtures predating it, keep their previous behavior.
 */
export function isEnrollmentLocked(program: { enrollment_status?: string | null }): boolean {
    return program.enrollment_status === 'closed';
}

/**
 * Whether a program accepts new enrollment right now: operational AND unlocked. Ignores the
 * date window and per-parent rules — callers layer those on top.
 *
 * Mirrors the `enrollments` INSERT RLS policy, which is the real enforcement point; this is the
 * client-side version used to gate and label the UI, so the two must agree.
 *
 * Note this is the PARENT rule. Admins bypass the lock via the separate "Admins can do
 * everything" policy, so admin surfaces should not gate on it.
 */
export function acceptsNewEnrollment(
    program: { active: boolean; enrollment_status?: string | null },
): boolean {
    return program.active && !isEnrollmentLocked(program);
}

export interface LineItemConfig {
    id?: string;
    name: string;
    amount: number;
    description?: string;
    type: string;
    subtype?: string;
    program_id?: string; // For UI purposes, not in the DB table
    created_at?: string;
    updated_at?: string;
}

export interface Program {
    id: string;
    name: string;
    description?: string; // Optional detailed description of the program
    waiver?: string; // Optional waiver/disclaimer content (Markdown/plain text)
    type: ProgramType;
    line_items?: LineItemConfig[];
    price: number; // Now calculated from line items
    billing_type: 'recurring_monthly' | 'fixed_term' | 'per_day' | 'flex_monthly';
    /**
     * Which brand sells this program — the only brand column in the schema; enrollment and
     * invoice brand derive from it. Optional because rows selected before this column existed
     * (and older fixtures) omit it; treat missing as `DEFAULT_BRAND_KEY`, which is also the DB
     * default. Presentation and reporting scope only, never an access predicate.
     */
    brand_key?: BrandKey;
    /** Whether the program is operational at all: billing, staff rosters, attendance. */
    active: boolean;
    /**
     * Whether the program accepts NEW enrollment. Orthogonal to `active` — a program with
     * `active: true` and `enrollment_status: 'closed'` is "active but locked": it keeps running
     * and billing for existing enrollments, but is closed to new signups. Optional because rows
     * selected before this column existed (and older fixtures) omit it; treat missing as 'open'.
     */
    enrollment_status?: ProgramEnrollmentStatus;
    start_date: string; // ISO date string
    end_date: string; // ISO date string
    schedule_cron: string;
    duration: number; // Duration in minutes
    levels?: string[] | null; // Optional array of levels (admin-defined)
    selective_dates?: string[] | null; // Optional whitelist of selectable dates (YYYY-MM-DD) for per_day programs
    excluded_dates?: string[] | null; // Optional per-program no-class dates (YYYY-MM-DD); vendor days off beyond the school-wide calendar
    alternative_dates?: Array<{ date: string; notes: string }>; // Alternative start dates
    invoice_due_date?: string | null; // Explicit invoice due date (YYYY-MM-DD) for camp programs; null = billing-type default
    /**
     * fixed_term only: a late joiner picks their first class and pays for the classes from it to
     * the end of the term (BHCS-152). Optional because rows selected before this column existed
     * omit it; treat missing as false, which is also the DB default.
     */
    prorate_late_joins?: boolean;
    /**
     * Seats: how many children may be enrolled at once (BHCS-124). Null or missing = unlimited,
     * which is also the DB default. The database refuses a parent's enrollment past it; admins
     * may go over on purpose. Live counts come from `program_capacity_status`.
     */
    capacity?: number | null;
    marketing_metadata?: { website?: string; poster_image?: string } | null;
    tags?: string[] | null;
    /** Where the program meets (`locations.id`). Optional; null = no location on file. */
    location_id?: string | null;
    /** Present when the select included `PROGRAM_LOCATION_EMBED`; read via `programLocationName`. */
    location?: ProgramLocation | ProgramLocation[] | null;
    created_at: string;
    updated_at: string;
}

// Pricing shapes shared across components
export interface PricingLineItemConfig {
    id: string;
    name: string;
    type?: string;
    description: string;
    subtype?: string;
}

export interface ProgramPricingItem {
    amount: number;
    is_optional: boolean;
    line_item_config: PricingLineItemConfig;
}

// Supabase row shape when selecting pricing with joined line_item_configs
export type DatabasePricingRow = {
    amount: number;
    is_optional: boolean;
    line_item_configs: Array<{
        id: string;
        name: string;
        type: string;
        description: string;
        subtype: string | null;
    }>;
};

export type ProgramWithPricing = Program & { pricing?: ProgramPricingItem[] };

export interface Enrollment {
    id: string;
    student_id: string;
    program_id: string;
    enrollment_start_date: string; // ISO date string
    enrollment_end_date: string; // ISO date string
    /**
     * Structured enrollment metadata — and the only home for the per-day dates and the
     * billing opt-outs. Both had columns of their own once; nothing wrote to them for a long
     * time, and code that reached for the column got an empty array back from a database
     * that had the answer one field away. Read `metadata.selected_days` through
     * `selectedDaysOf()`, never a column.
     */
    metadata?: EnrollmentMetadata;
    is_active: boolean; // Whether this enrollment is active for billing and display
    created_at: string;
    updated_at: string;
}

// Enrollment metadata shape stored in enrollments.metadata
export interface EnrollmentMetadata {
    signed_waiver?: {
        name: string;
        date: string; // YYYY-MM-DD (capture date of signature)
    };
    /** The days a `per_day` enrollment bought, as `YYYY-MM-DD`. The billing source of truth. */
    selected_days?: string[];
    /** `line_item_configs` ids this family pays for. The billing source of truth for
     *  optional rows; anything not listed here is simply not charged. */
    opt_in_items?: string[];
    [key: string]: unknown;
}

/**
 * The days a per-day enrollment bought, and the line items it opted out of.
 *
 * Accessors rather than a `metadata.selected_days ?? []` at each call site, because this is
 * exactly the shape that rots quietly: every page hydrated it by hand, and a page that forgot
 * showed a camp enrollment with no dates at all rather than an error.
 *
 * Tolerant of the row shape on purpose — admin pages, the parent portal and the CLI all pass
 * a differently-typed enrollment through here.
 */
/**
 * The `line_item_configs` a family pays for, and the only record billing consults.
 *
 * It replaced `opt_out_items`, which stored the complement — the ids declined. That only
 * meant anything against the optional rows the program offered at the instant of enrollment,
 * so adding an optional row to a live program silently re-interpreted every older enrollment
 * as having accepted it.
 *
 * Returns `null` (not `[]`) when the key is absent, so a caller can tell "chose nothing" from
 * "nothing recorded". Billing treats both as "charge nothing optional".
 */
export function optInItemsOf(
    enrollment: { metadata?: { opt_in_items?: unknown } | null } | null | undefined,
): string[] | null {
    const items = enrollment?.metadata?.opt_in_items;
    if (!Array.isArray(items)) return null;
    return items.filter((i): i is string => typeof i === 'string');
}

/**
 * The `line_item_configs` a family opted out of. Same story as the days above: metadata is
 * the source the invoice trigger reads, and the column it once had is gone.
 */
export function selectedDaysOf(
    enrollment: { metadata?: { selected_days?: unknown } | null } | null | undefined,
): string[] {
    const days = enrollment?.metadata?.selected_days;
    return Array.isArray(days) ? days.filter((d): d is string => typeof d === 'string') : [];
}

// Flex monthly billing: one editable/lockable day-plan per (enrollment, month).
// `dates` is the authoritative billed set for the month ([] = intentional pause).
export type FlexPlanStatus = 'draft' | 'locked' | 'billed' | 'skipped';

export interface FlexMonthPlan {
    id: string;
    enrollment_id: string;
    plan_month: string; // YYYY-MM-01 — first of the billed month
    dates: string[];     // confirmed YYYY-MM-DD dates within plan_month
    status: FlexPlanStatus;
    locked_at: string | null;
    updated_by: string | null; // 'parent:<id>' | 'admin:<email>' | 'system'
    created_at: string;
    updated_at: string;
}

// Interface for invoice objects
export interface Invoice {
    id: string;
    enrollment_id: string;
    total_amount: number;
    amount_paid: number;
    status: 'pending' | 'partial' | 'paid' | 'overdue' | 'voided';
    due_date: string;
    period_start: string;
    period_end: string;
    is_prorated?: boolean;
    created_at: string;
    updated_at: string;
    payments?: { status: string; payment_method?: string }[];
}

export type PaymentStatus = 'paid' | 'partial' | 'pending' | 'overdue' | 'voided' | 'no_invoice' | 'payment_processing';

// Narrow type used only for payment status logic where we just need optional program price context
type EnrollmentLikeForStatus = Partial<Enrollment> & { programs?: { price?: number } };

const NO_CART_HOLDS: ReadonlySet<string> = new Set();

// Helper function to get payment status from invoice
export function getEnrollmentPaymentStatus(
    _enrollment: Enrollment | EnrollmentWithDetails | EnrollmentLikeForStatus,
    invoices: Invoice[],
    heldByCart: ReadonlySet<string> = NO_CART_HOLDS
): PaymentStatus {
    if (!invoices || invoices.length === 0) {
        return 'no_invoice';
    }

    // Only surface VOIDED when all related invoices are voided
    const allVoided = invoices.every(inv => (inv.status || '').toLowerCase() === 'voided');
    if (allVoided) {
        return 'voided';
    }

    // An invoice is "processing" when it has any in-flight payment regardless of
    // invoice status. For ACH+BHCS Cash splits the wallet portion is applied
    // immediately (invoice → 'partial'), while ACH remains in 'processing' for
    // 1–3 days. Requiring status='pending' would miss that case. A bank bundle payment
    // still clearing has no payments row yet; `heldByCart` (useCartHeldInvoiceIds) covers it.
    const isPaymentProcessing = invoices.some(inv => isPaymentInFlight(inv, heldByCart));

    if (isPaymentProcessing) {
        return 'payment_processing';
    }

    // Ignore voided invoices when computing aggregate payment totals
    const nonVoidedInvoices = invoices.filter(inv => (inv.status || '').toLowerCase() !== 'voided');
    const totalAmount = nonVoidedInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
    const totalPaid = nonVoidedInvoices.reduce((sum, inv) => sum + inv.amount_paid, 0);

    if (totalPaid === totalAmount) {
        return 'paid';
    }

    if (totalPaid > 0) {
        return 'partial';
    }

    return 'pending';
}

/**
 * Converts a program name to a URL-friendly slug
 * @param name - The program name to convert
 * @returns A URL-safe slug string
 * 
 * Examples:
 * "Good behavior 2" → "good-behavior-2"
 * "Math & Science Club" → "math-science-club"
 * "Art & Craft Workshop!" → "art-craft-workshop"
 * "Advanced Mathematics (Level 3)" → "advanced-mathematics-level-3"
 */
export function programNameToSlug(name: string): string {
    return name
        // Convert to lowercase
        .toLowerCase()
        // Replace special characters and spaces with hyphens
        .replace(/[^a-z0-9\s-]/g, '')
        // Replace multiple spaces and hyphens with single hyphens
        .replace(/[\s-]+/g, '-')
        // Remove leading/trailing hyphens
        .replace(/^-+|-+$/g, '')
        // Ensure the slug is not empty
        .replace(/^$/, 'program');
}

/**
 * Generates a unique slug for a program, handling conflicts by appending numbers
 * @param name - The program name to convert
 * @param existingSlugs - Array of existing slugs to check against
 * @returns A unique slug string
 * 
 * Examples:
 * "Good behavior" → "good-behavior" (if unique)
 * "Good behavior" → "good-behavior-1" (if "good-behavior" exists)
 * "Good behavior" → "good-behavior-2" (if both exist)
 */
export function generateUniqueProgramSlug(name: string, existingSlugs: string[]): string {
    const baseSlug = programNameToSlug(name);
    
    if (!existingSlugs.includes(baseSlug)) {
        return baseSlug;
    }
    
    // Find the next available number
    let counter = 1;
    let slug = `${baseSlug}-${counter}`;
    
    while (existingSlugs.includes(slug)) {
        counter++;
        slug = `${baseSlug}-${counter}`;
    }
    
    return slug;
}

/**
 * Finds a program by its slug from a list of programs
 * @param programs - Array of programs to search through
 * @param slug - The slug to search for
 * @returns The program if found, undefined otherwise
 */
export function findProgramBySlug<T extends { name: string }>(programs: T[], slug: string): T | undefined {
    return programs.find(program => programNameToSlug(program.name) === slug);
}

/**
 * Gets all available slugs from a list of programs
 * @param programs - Array of programs to extract slugs from
 * @returns Array of unique slugs
 */
export function getAllProgramSlugs<T extends { name: string }>(programs: T[]): string[] {
    return programs.map(program => programNameToSlug(program.name));
}

// Utility type for creating a new program
export type CreateProgram = Omit<Program, 'id' | 'created_at' | 'updated_at'>;

// Utility type for updating a program
export type UpdateProgram = Partial<CreateProgram>;

// Utility type for creating a new enrollment
export type CreateEnrollment = Omit<Enrollment, 'id' | 'created_at' | 'updated_at'>;

// Utility type for updating an enrollment
export type UpdateEnrollment = Partial<Omit<Enrollment, 'id' | 'created_at' | 'updated_at'>>;

// Common cron expressions for different schedules
export const CronExamples = {
    WEEKLY_MON_WED_FRI: '0 15 * * 1,3,5',  // Every Monday, Wednesday, Friday at 3 PM
    WEEKLY_TUE_THU: '0 16 * * 2,4',        // Every Tuesday, Thursday at 4 PM
    DAILY: '0 14 * * *',                   // Every day at 2 PM
    BI_WEEKLY: '0 15 * * 1,3',             // Every Monday and Wednesday at 3 PM
    WEEKDAYS: '0 9 * * 1-5',               // Every weekday (Monday to Friday) at 9 AM
} as const;

// Helper function to validate cron expression
export function isValidCronExpression(cron: string): boolean {
    // If cron is empty, it's invalid
    if (!cron || !cron.trim()) {
        return false;
    }

    // Check if it has 5 parts (minute, hour, day of month, month, day of week)
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) {
        return false;
    }

    // Try more permissive validation instead
    try {
        // Just check for 5 parts, each with reasonable values
        const minute = parseInt(parts[0]);
        if (parts[0] !== '*' && parts[0].indexOf('-') === -1 && parts[0].indexOf(',') === -1 && parts[0].indexOf('/') === -1 && (isNaN(minute) || minute < 0 || minute > 59)) {
            return false;
        }

        const hour = parseInt(parts[1]);
        if (parts[1] !== '*' && parts[1].indexOf('-') === -1 && parts[1].indexOf(',') === -1 && parts[1].indexOf('/') === -1 && (isNaN(hour) || hour < 0 || hour > 23)) {
            return false;
        }

        const dayOfMonth = parseInt(parts[2]);
        if (parts[2] !== '*' && parts[2].indexOf('-') === -1 && parts[2].indexOf(',') === -1 && parts[2].indexOf('/') === -1 && (isNaN(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31)) {
            return false;
        }

        const month = parseInt(parts[3]);
        if (parts[3] !== '*' && parts[3].indexOf('-') === -1 && parts[3].indexOf(',') === -1 && parts[3].indexOf('/') === -1 && (isNaN(month) || month < 1 || month > 12)) {
            return false;
        }

        const dayOfWeek = parseInt(parts[4]);
        if (parts[4] !== '*' && parts[4].indexOf('-') === -1 && parts[4].indexOf(',') === -1 && parts[4].indexOf('/') === -1 && (isNaN(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 7)) {
            return false;
        }

        // Let's also check for common range patterns
        for (const part of parts) {
            if (part.includes('-')) {
                const [start, end] = part.split('-').map(n => parseInt(n));
                if (isNaN(start) || isNaN(end) || start > end) {
                    return false;
                }
            }
        }

        return true;
    } catch (e) {
        return false;
    }
}

// Helper function to get human-readable schedule description
export function getScheduleDescription(cron: string): string {
    // Validate the cron expression first
    if (!isValidCronExpression(cron)) {
        return "Invalid schedule format";
    }

    const [minute, hour, dayOfMonth, month, dayOfWeek] = cron.split(' ');

    try {
        // Handle range expressions in day of week
        const processDayOfWeek = (dow: string) => {
            // For range expressions like 1-5
            if (dow.includes('-')) {
                const [start, end] = dow.split('-').map(Number);
                const days = [];
                for (let i = start; i <= end; i++) {
                    days.push(i.toString());
                }
                return days;
            }
            // For comma-separated values
            if (dow.includes(',')) {
                return dow.split(',');
            }
            // For single values
            return [dow];
        };

        const dayNumbers = dayOfWeek.includes('-') || dayOfWeek.includes(',') ?
            processDayOfWeek(dayOfWeek) : [dayOfWeek];

        const days = dayNumbers.map(d => {
            switch(d) {
                case '0':
                case '7': return 'Sunday';
                case '1': return 'Monday';
                case '2': return 'Tuesday';
                case '3': return 'Wednesday';
                case '4': return 'Thursday';
                case '5': return 'Friday';
                case '6': return 'Saturday';
                default: return d;
            }
        });

        // Same for months with range expressions
        const processMonth = (m: string) => {
            if (m.includes('-')) {
                const [start, end] = m.split('-').map(Number);
                const months = [];
                for (let i = start; i <= end; i++) {
                    months.push(i.toString());
                }
                return months;
            }
            if (m.includes(',')) {
                return m.split(',');
            }
            return [m];
        };

        const monthNumbers = month.includes('-') || month.includes(',') ?
            processMonth(month) : [month];

        const months = monthNumbers.map(m => {
            switch(m) {
                case '1': return 'January';
                case '2': return 'February';
                case '3': return 'March';
                case '4': return 'April';
                case '5': return 'May';
                case '6': return 'June';
                case '7': return 'July';
                case '8': return 'August';
                case '9': return 'September';
                case '10': return 'October';
                case '11': return 'November';
                case '12': return 'December';
                default: return m;
            }
        });

        // Format the time safely
        const formattedHour = parseInt(hour, 10);
        const formattedMinute = parseInt(minute, 10);
        const time = isNaN(formattedHour) || isNaN(formattedMinute)
            ? `${hour}:${minute}`
            : `${formattedHour}:${formattedMinute.toString().padStart(2, '0')}`;

        let description = '';

        // Handle day of month
        if (dayOfMonth !== '*') {
            description += `On day ${dayOfMonth} `;
        }

        // Handle month
        if (month !== '*') {
            if (description) description += 'of ';
            description += `${months.join(', ')} `;
        }

        // Handle day of week
        if (dayOfWeek === '*') {
            description += `Every day`;
        } else {
            description += `Every ${days.join(', ')}`;
        }

        description += ` at ${time}`;

        return description;
    } catch (error) {
        // If parsing fails for any reason, return a generic message
        return "Custom schedule";
    }
}

// Emoji icon per program type. Accepts a raw string to stay resilient to DB values.
export function getProgramTypeIcon(type: string): string {
    return getProgramTypeMeta(type)?.icon ?? '📝';
}

// Decorative single Chinese character per program type.
// Inspired by the marketing site (bhcskids.com/school-year), where each program
// pillar is anchored by one bold glyph. Used as a decorative accent only — the
// English label always carries the actual meaning (glyphs are aria-hidden).
export function getProgramTypeGlyph(type: string): string {
    return getProgramTypeMeta(type)?.glyph ?? '余'; // 余 — the rest
}

/**
 * Brand accent color, matching the marketing palette. Shared between the parent-facing
 * Programs section headers and the program cards so the accent stays consistent.
 * Types without a brand color (and unrecognized values) fall back to the deep blue.
 */
export function getProgramTypeColor(type: string): string {
    return getProgramTypeMeta(type)?.color ?? '#244868';
}

/**
 * Same accent, but for admin section headers, where a type with no brand color reads
 * better as neutral gray than as the deep-blue default.
 */
export function getProgramTypeSectionColor(type: string): string {
    return getProgramTypeMeta(type)?.color ?? '#6b7280';
}

/** Tailwind border/text/bg classes for the program-type badge chip. */
export function getProgramTypeBadgeClasses(type: string): string {
    return getProgramTypeMeta(type)?.badge ?? BADGE_NEUTRAL;
}

// Recommended font stack for rendering decorative CJK glyphs crisply across platforms.
export const CJK_GLYPH_FONT_FAMILY =
    '"PingFang SC", "Noto Sans SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif';

// Helper function to format duration in minutes to a human-readable string
export function formatDuration(minutes?: number | null): string {
    if (minutes === undefined || minutes === null) {
        return "Not specified";
    }

    // Special case: exactly 60 minutes (1 hour)
    if (minutes === 60) {
        return "1 hour";
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    let result = "";
    if (hours > 0) {
        result += `${hours} hour${hours !== 1 ? 's' : ''}`;
    }

    if (remainingMinutes > 0) {
        if (result) result += " ";
        result += `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
    }

    return result || "0 minutes";
}

// Helper function to display a program's schedule as a time range
export function getScheduleTimeRange(cron: string): string {
    try {
        const [, , , , dayOfWeek] = cron.split(' ');

        // Check if we have valid parts
        if (!dayOfWeek || cron.split(' ').filter(Boolean).length !== 5) {
            return "Custom schedule";
        }

        const days = {
            '0': 'Sunday',
            '1': 'Monday',
            '2': 'Tuesday',
            '3': 'Wednesday',
            '4': 'Thursday',
            '5': 'Friday',
            '6': 'Saturday',
            '7': 'Sunday'  // Both 0 and 7 can represent Sunday
        };

        // Handle range expressions (e.g., 1-5 for Monday to Friday)
        let dayNames: string[] = [];

        if (dayOfWeek === '*') {
            dayNames = ['Every day'];
        } else if (dayOfWeek.includes('-')) {
            // Handle range like 1-5
            const [start, end] = dayOfWeek.split('-').map(Number);
            if (!isNaN(start) && !isNaN(end)) {
                for (let i = start; i <= end; i++) {
                    const dayKey = i.toString() as keyof typeof days;
                    const day = days[dayKey];
                    if (day) dayNames.push(day);
                }
            }
        } else if (dayOfWeek.includes(',')) {
            // Handle comma-separated days
            const dayNumbers = dayOfWeek.split(',');
            dayNames = dayNumbers.map(num => days[num as keyof typeof days] || 'Unknown day');
        } else {
            // Handle single day
            dayNames = [days[dayOfWeek as keyof typeof days] || 'Unknown day'];
        }

        if (dayNames.length === 0) {
            return "Custom schedule";
        } else if (dayNames[0] === 'Every day') {
            return `Every day`;
        } else if (dayNames.length === 1) {
            return `Every ${dayNames[0]}`;
        } else {
            // Special case: if we have exactly Monday through Friday, display as "Weekdays"
            if (dayNames.length === 5 &&
                dayNames.includes('Monday') &&
                dayNames.includes('Tuesday') &&
                dayNames.includes('Wednesday') &&
                dayNames.includes('Thursday') &&
                dayNames.includes('Friday')) {
                return `Weekdays`;
            }

            const lastDay = dayNames.pop();
            return `Every ${dayNames.join(', ')} and ${lastDay}`;
        }
    } catch (error) {
        return "Custom schedule";
    }
}

// Compact schedule: "MON WED FRI" instead of "Every Monday, Wednesday and Friday"
export function getScheduleShort(cron: string): string {
    try {
        const [, , , , dayOfWeek] = cron.split(' ');
        if (!dayOfWeek || cron.split(' ').filter(Boolean).length !== 5) return 'Custom';

        const abbr: Record<string, string> = {
            '0': 'SUN', '1': 'MON', '2': 'TUE', '3': 'WED',
            '4': 'THU', '5': 'FRI', '6': 'SAT', '7': 'SUN'
        };

        if (dayOfWeek === '*') return 'Daily';

        // Split on comma first so combined expressions like "1-5,7" are handled correctly
        const keys: string[] = [];
        for (const part of dayOfWeek.split(',')) {
            if (part.includes('-')) {
                const [start, end] = part.split('-').map(Number);
                if (!isNaN(start) && !isNaN(end)) {
                    for (let i = start; i <= end; i++) keys.push(i.toString());
                }
            } else {
                keys.push(part);
            }
        }

        const names = keys.map(k => abbr[k]).filter(Boolean);
        if (names.length === 0) return 'Custom';
        if (names.length === 5 && ['MON','TUE','WED','THU','FRI'].every(d => names.includes(d))) return 'Weekdays';
        return names.join(' ');
    } catch {
        return 'Custom';
    }
}

/**
 * A cron's minute, hour and weekdays.
 *
 * Returns no days at all for a string this cannot read, rather than throwing. Four call sites
 * already pass `parseCron(schedule || '')`, which says plainly what they expect of an absent
 * schedule — and until now got a TypeError instead, because `''.split(' ')` leaves the
 * weekday field undefined and the branches below call `.includes` on it.
 */
export function parseCron(cron: string) {
  const [minute, hour, , , dayOfWeek] = (cron ?? '').split(' ');

  let days: number[] = [];

  if (dayOfWeek === undefined) {
    days = [];
  } else if (dayOfWeek === '*') {
    days = [1, 2, 3, 4, 5, 6, 7]; // All days
  } else if (dayOfWeek.includes('-')) {
    // Handle range expressions like 1-5
    const [start, end] = dayOfWeek.split('-').map(Number);
    for (let i = start; i <= end; i++) {
      days.push(i);
    }
  } else if (dayOfWeek.includes(',')) {
    // Handle comma-separated values
    days = dayOfWeek.split(',').map(Number);
  } else {
    // Single day
    days = [Number(dayOfWeek)];
  }

  return { minute: Number(minute), hour: Number(hour), days };
}

export interface EnrollmentWithDetails extends Enrollment {
    students: {
        first_name: string;
        last_name: string;
        date_of_birth: string;
    };
    programs: Program;
}

// English display name. Unrecognized values are echoed back unchanged.
export function getProgramTypeDisplayName(type: string): string {
    return getProgramTypeMeta(type)?.label ?? type;
}